/**
 * Dependency-free API error event bus.
 *
 * `api-error.ts` imports the Axios client's `ApiError` class. Keeping the bus
 * here lets the client publish errors through a static import without a
 * client/presentation cycle or an ineffective dynamic import.
 */
export interface ApiErrorEvent {
  error: unknown;
  /** Calling operation, for example "签字失败". */
  context?: string;
}

type Listener = (event: ApiErrorEvent) => void;

const listeners = new Set<Listener>();

export function onApiError(listener: Listener): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function emitApiError(error: unknown, context?: string): void {
  // Copy the snapshot because a listener may unsubscribe while dispatching.
  for (const listener of [...listeners]) {
    try {
      listener({ error, context });
    } catch {
      // A faulty listener must never prevent the remaining notifications.
    }
  }
}

/** Test-only reset. Production code must not call this. */
export function _clearApiErrorListeners(): void {
  listeners.clear();
}
